import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  TextField,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore,
  Info,
  CheckCircle
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../lib/api';
import { Component } from '../lib/types';

const ComponentSelectionPage: React.FC = () => {
  const [selectedComponents, setSelectedComponents] = useState<Record<string, boolean>>({});
  const [categories, setCategories] = useState<Record<string, Component[]>>({});
  const [customRequirements, setCustomRequirements] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const navigate = useNavigate();

  // Fetch components
  const { data: componentsData, isLoading } = useQuery({
    queryKey: ['components'],
    queryFn: async () => {
      const response = await api.get('/components');
      return response.data.data;
    },
    onSuccess: (data) => {
      // Group by category
      const grouped = data.reduce((acc: Record<string, Component[]>, component: Component) => {
        if (!acc[component.category]) {
          acc[component.category] = [];
        }
        acc[component.category].push(component);
        return acc;
      }, {});
      setCategories(grouped);
    }
  });

  const selectedCount = Object.values(selectedComponents).filter(Boolean).length;

  const handleToggleComponent = (componentId: string) => {
    setSelectedComponents(prev => ({
      ...prev,
      [componentId]: !prev[componentId]
    }));
  };

  const createAgreementMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/agreements', data);
      return response.data;
    },
    onSuccess: () => {
      setShowConfirmDialog(false);
      navigate('/dashboard');
    }
  });

  const handleSubmit = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = () => {
    const componentIds = Object.entries(selectedComponents)
      .filter(([, selected]) => selected)
      .map(([id]) => id);

    const agreementData = {
      type: 'INDIVIDUAL',
      clientIds: [], // Will need to get current user's ID
      selectedComponents: componentIds.map(id => ({
        componentId: id,
        clientId: null
      })),
      customRequirements
    };

    createAgreementMutation.mutate(agreementData);
  };

  if (isLoading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box mb={4}>
          <Typography variant="h4" gutterBottom>
            🎓 Customize Your Trust Learning Journey
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" paragraph>
            Select the trust components you'd like to explore and implement in your 90-day program.
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <Chip
              label={`${selectedCount} components selected`}
              color="primary"
              icon={<CheckCircle />}
            />
          </Box>
        </Box>

        {/* Component Categories */}
        {Object.entries(categories).map(([category, components]) => (
          <Accordion key={category} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {category}
                </Typography>
                <Chip
                  label={`${components.length} components`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {components.map((component) => (
                  <Grid item xs={12} key={component.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!selectedComponents[component.id]}
                              onChange={() => handleToggleComponent(component.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {component.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {component.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Custom Requirements */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Additional Learning Objectives
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Describe any specific trust topics, strategies, or goals you'd like to explore beyond the standard components.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Example: Tax mitigation strategies, asset protection techniques, estate planning best practices..."
            value={customRequirements}
            onChange={(e) => setCustomRequirements(e.target.value)}
          />
        </Box>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button variant="outlined" onClick={() => navigate('/dashboard')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={selectedCount === 0 || createAgreementMutation.isLoading}
          >
            {createAgreementMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Start My Trust Journey'
            )}
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Start Your Trust Launch Journey?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You're about to start a 90-day trust implementation program with{' '}
            <strong>{selectedCount} components</strong> selected. This will create your
            personalized learning path with milestones and tasks to guide you through each phase.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} variant="contained">
            Start Journey
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ComponentSelectionPage;

